import type {
  AdminJitActivation,
  AdminJitActivationRequest,
  AdminJitEligibilityInput,
  AdminJitMode,
  AdminJitMyAccess,
  AdminJitStatus,
} from '../api/adminConsole';

// Just-in-time access views (RBAC Phase 4 workstream A). admin-api decides eligibility, approval
// and expiry; these helpers only shape what the pages show and mirror the server's form rules so
// a mistake is caught before the request. Keep the limits in step with admin-api
// `domain/iam/jitAccess.ts`.

export const JIT_MIN_DURATION_MINUTES = 15;
export const JIT_MAX_DURATION_MINUTES = 480;
export const JIT_DEFAULT_DURATION_MINUTES = 240;
/** A pending request nobody decided within this window can no longer be approved. */
export const JIT_REQUEST_TTL_MS = 24 * 60 * 60 * 1000;

const DURATION_STEPS = [15, 30, 60, 120, 240, 480] as const;

const toMs = (v: string | null | undefined) => (v ? Date.parse(v) : NaN);

function formatSpan(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

/** Time left until `until`: "2h 05m", "45m", "less than a minute", "expired", or "—" when unknown. */
export function formatRemaining(until: string | null | undefined, now: number = Date.now()): string {
  const t = toMs(until);
  if (Number.isNaN(t)) return '—';
  const left = t - now;
  if (left <= 0) return 'expired';
  if (left < 60_000) return 'less than a minute';
  return formatSpan(left);
}

export type MyAccessRow = {
  eligibilityId: string;
  roleId: string;
  roleKey: string;
  roleName: string;
  state: 'eligible' | 'pending' | 'active';
  activationId: string | null;
  /** Time left for an active activation. */
  remaining: string | null;
  expiresAt: string | null;
  reportOnly: boolean;
  requiresApproval: boolean;
  maxDurationMinutes: number;
  reasonRequired: boolean;
  /** Activating needs a passkey sign-in (ADMIN_PHR_MODE=enforce lists the role; this session has none). */
  passkeyRequired: boolean;
};

const STATE_ORDER: Record<MyAccessRow['state'], number> = { active: 0, pending: 1, eligible: 2 };

/**
 * One row per eligibility: active first, then pending, then eligible. An activation whose expiry
 * has passed on this clock is shown as eligible again (admin-api agrees on its next read).
 */
export function myAccessRows(access: AdminJitMyAccess | undefined, now: number = Date.now()): MyAccessRow[] {
  return (access?.eligibilities ?? [])
    .map((e): MyAccessRow => {
      const a = e.activation;
      let state: MyAccessRow['state'] = 'eligible';
      if (a?.displayStatus === 'pending') state = 'pending';
      if (a?.displayStatus === 'active' && toMs(a.expiresAt) > now) state = 'active';
      return {
        eligibilityId: e.id,
        roleId: e.roleId,
        roleKey: e.roleKey,
        roleName: e.roleName,
        state,
        activationId: state === 'eligible' ? null : a!.id,
        remaining: state === 'active' ? formatRemaining(a!.expiresAt, now) : null,
        expiresAt: state === 'active' ? a!.expiresAt : null,
        reportOnly: state === 'active' && a!.reportOnly,
        requiresApproval: e.requiresApproval,
        maxDurationMinutes: e.maxDurationMinutes,
        reasonRequired: e.reasonRequired,
        passkeyRequired: e.passkeyRequired === true,
      };
    })
    .sort((x, y) => STATE_ORDER[x.state] - STATE_ORDER[y.state] || x.roleName.localeCompare(y.roleName));
}

export type ActivationBannerItem = { activationId: string; roleName: string; remaining: string; reportOnly: boolean };

/** Roles the viewer has activated and still holds, soonest expiry first (the layout banner). */
export function activationBannerItems(access: AdminJitMyAccess | undefined, now: number = Date.now()): ActivationBannerItem[] {
  return myAccessRows(access, now)
    .filter((r) => r.state === 'active')
    .sort((x, y) => toMs(x.expiresAt) - toMs(y.expiresAt))
    .map((r) => ({ activationId: r.activationId!, roleName: r.roleName, remaining: r.remaining!, reportOnly: r.reportOnly }));
}

export function canActivate(mode: AdminJitMode | undefined, row: Pick<MyAccessRow, 'state'>): { allowed: true } | { allowed: false; reason: string } {
  if (!mode || mode === 'off') return { allowed: false, reason: 'Just-in-time access is turned off.' };
  if (row.state === 'pending') return { allowed: false, reason: 'A request for this role is waiting for approval.' };
  if (row.state === 'active') return { allowed: false, reason: 'This role is already active.' };
  return { allowed: true };
}

export const PASSKEY_ACTIVATION_NOTICE =
  'Activating this role needs a passkey sign-in. When you activate, you will be asked to sign in again with your passkey (register one first if you have none).';

/**
 * The passkey requirement, said before the click (RBAC Phase 4 B). The button stays enabled: the
 * request answers 403 PHR_REQUIRED and the client re-authorizes with acr_values=phr, which is how
 * the user gets a passkey session in the first place.
 */
export function activationPasskeyNotice(row: Pick<MyAccessRow, 'state' | 'passkeyRequired'>): string | null {
  return row.state === 'eligible' && row.passkeyRequired ? PASSKEY_ACTIVATION_NOTICE : null;
}

/** Duration choices up to the eligibility's cap; the cap itself is always offered. */
export function durationOptions(maxMinutes: number): number[] {
  const cap = Math.min(Math.max(maxMinutes, JIT_MIN_DURATION_MINUTES), JIT_MAX_DURATION_MINUTES);
  const steps: number[] = DURATION_STEPS.filter((d) => d <= cap);
  if (!steps.includes(cap)) steps.push(cap);
  return steps;
}

export function defaultActivationDuration(maxMinutes: number): number {
  return Math.min(JIT_DEFAULT_DURATION_MINUTES, maxMinutes);
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = minutes / 60;
  return Number.isInteger(h) ? `${h} hour${h === 1 ? '' : 's'}` : `${Math.floor(h)}h ${minutes % 60}m`;
}

export type ActivationDraft = { reason: string; ticket: string; durationMinutes: number };

export function validateActivationDraft(
  draft: ActivationDraft,
  row: Pick<MyAccessRow, 'roleId' | 'reasonRequired' | 'maxDurationMinutes'>,
): { ok: true; body: AdminJitActivationRequest } | { ok: false; errors: { reason?: string; durationMinutes?: string } } {
  const errors: { reason?: string; durationMinutes?: string } = {};
  const reason = draft.reason.trim();
  const ticket = draft.ticket.trim();
  if (row.reasonRequired && !reason) errors.reason = 'Say why you need this role.';
  if (reason.length > 1000) errors.reason = 'Keep the reason under 1000 characters.';
  const d = draft.durationMinutes;
  if (!Number.isInteger(d) || d < 1 || d > Math.min(row.maxDurationMinutes, JIT_MAX_DURATION_MINUTES)) {
    errors.durationMinutes = `Choose up to ${formatDuration(Math.min(row.maxDurationMinutes, JIT_MAX_DURATION_MINUTES))}.`;
  }
  if (errors.reason || errors.durationMinutes) return { ok: false, errors };
  return {
    ok: true,
    body: { roleId: row.roleId, ...(reason ? { reason } : {}), ...(ticket ? { ticket: ticket.slice(0, 200) } : {}), durationMinutes: d },
  };
}

export type ApprovalRow = AdminJitActivation & { requestedAgo: string; expiresIn: string };

/** Pending requests with their age and how long before they expire undecided. */
export function approvalRows(items: readonly AdminJitActivation[], now: number = Date.now()): ApprovalRow[] {
  return items.map((a) => {
    const requested = toMs(a.requestedAt);
    const age = Number.isNaN(requested) ? NaN : now - requested;
    return {
      ...a,
      requestedAgo: Number.isNaN(age) ? '—' : age < 60_000 ? 'just now' : `${formatSpan(age)} ago`,
      expiresIn: Number.isNaN(requested) ? '—' : formatRemaining(new Date(requested + JIT_REQUEST_TTL_MS).toISOString(), now),
    };
  });
}

export function jitModeNotice(mode: AdminJitMode | undefined): { severity: 'warning' | 'info'; text: string } | null {
  if (!mode || mode === 'off') {
    return { severity: 'warning', text: 'Just-in-time access is turned off in this environment. You cannot activate roles yet.' };
  }
  if (mode === 'report_only') {
    return {
      severity: 'info',
      text: 'Report-only rollout: activations are recorded and audited, and standing access is unchanged for now.',
    };
  }
  return null;
}

export function activationStatusChip(status: AdminJitStatus): {
  label: string;
  color: 'default' | 'success' | 'warning' | 'error' | 'info';
} {
  switch (status) {
    case 'pending':
      return { label: 'Pending approval', color: 'warning' };
    case 'active':
      return { label: 'Active', color: 'success' };
    case 'rejected':
      return { label: 'Rejected', color: 'error' };
    case 'cancelled':
      return { label: 'Cancelled', color: 'default' };
    case 'ended':
      return { label: 'Ended early', color: 'info' };
    default:
      return { label: 'Expired', color: 'default' };
  }
}

/** The server's default approver: Super Admin and IAM Admin approve themselves, else IAM Admin. */
export function defaultApproverRoleId(roleKey: string, roles: ReadonlyArray<{ id: string; key: string }>): string {
  const approverKey = roleKey === 'super_admin' || roleKey === 'iam_admin' ? roleKey : 'iam_admin';
  return roles.find((r) => r.key === approverKey)?.id ?? '';
}

export function validateEligibilityDraft(
  draft: AdminJitEligibilityInput | (Omit<AdminJitEligibilityInput, 'approverRoleId'> & { approverRoleId: string | null }),
): { ok: true; body: AdminJitEligibilityInput } | { ok: false; error: string } {
  if (!draft.userId) return { ok: false, error: 'Choose a user.' };
  if (!draft.roleId) return { ok: false, error: 'Choose a role.' };
  const d = draft.maxDurationMinutes;
  if (!Number.isInteger(d) || d < JIT_MIN_DURATION_MINUTES || d > JIT_MAX_DURATION_MINUTES) {
    return { ok: false, error: `Maximum duration must be ${JIT_MIN_DURATION_MINUTES}–${JIT_MAX_DURATION_MINUTES} minutes.` };
  }
  if (draft.requiresApproval && !draft.approverRoleId) return { ok: false, error: 'Choose the approver role.' };
  return { ok: true, body: { ...draft, approverRoleId: draft.requiresApproval ? draft.approverRoleId : null } };
}
